import {useCallback} from 'react'
import {addNode} from '../../editorActions'
import {EditorConfig} from '../../constants/editorConfig'
import DraftImageStore from '../../persistence/DraftImageStore'

const CURRENT_SESSION_DRAFT_ID = '_current'

/**
 * Hook that handles the "add image from device" flow:
 * 1. Creates a preview blob URL and adds a new image node instantly.
 * 2. Stores the File in runtime.pendingImageFiles for later upload on Save.
 * 3. Persists the File to IndexedDB so it survives page reload.
 *
 * @param {{ runtime: object, draftId?: string | null }} params
 * @returns {{ handlePickFile: (file: File) => void }}
 */
export function useAddImageApply({runtime, draftId}) {
    const handlePickFile = useCallback(
        (file) => {
            if (!file || !runtime?.dispatch) return

            const blobUrl = URL.createObjectURL(file)
            const nodeId = Date.now().toString()

            // Load image to get natural dimensions before adding node
            const img = new window.Image()
            img.onload = () => {
                const node = {
                    ...EditorConfig.DEFAULT_IMAGE_NODE,
                    id: nodeId,
                    content: {
                        ...EditorConfig.DEFAULT_IMAGE_NODE.content,
                        src: blobUrl,
                        naturalWidth: img.naturalWidth,
                        naturalHeight: img.naturalHeight,
                    },
                }
                runtime.dispatch(addNode(node, 0, true))
            }
            img.onerror = () => {
                // Fallback: add node without natural dimensions
                const node = {
                    ...EditorConfig.DEFAULT_IMAGE_NODE,
                    id: nodeId,
                    content: {...EditorConfig.DEFAULT_IMAGE_NODE.content, src: blobUrl},
                }
                runtime.dispatch(addNode(node, 0, true))
            }
            img.src = blobUrl

            if (runtime?.pendingImageFiles) {
                runtime.pendingImageFiles.set(nodeId, file)
            }

            const effectiveDraftId = draftId ?? CURRENT_SESSION_DRAFT_ID
            DraftImageStore.save(effectiveDraftId, nodeId, file).catch((err) => {
                console.error('Failed to save image file to IndexedDB', err)
            })
        },
        [runtime, draftId],
    )

    return {handlePickFile}
}
